import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { ImageIcon, Upload, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Company logo upload card. The uploaded image is stored server-side
 * under `/api/uploads/branding/company_logo.<ext>` and its URL is saved
 * in the `company_logo` setting key. Every in-house PDF generator
 * (hosting + AMC quotes, payslips) reads the same key so changing the
 * logo here propagates to every branded document instantly.
 *
 * Accepts PNG / JPG / WEBP up to 2MB. SVGs are intentionally rejected
 * client-side too because ReportLab can't rasterise them without extra
 * dependencies we don't ship.
 */
export const CompanyLogoCard = () => {
  const [logoUrl, setLogoUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);
  // Cache-buster — re-upload returns the same URL, so we tag a query
  // string to force the <img> to re-fetch instead of showing the old
  // file.
  const [cacheBust, setCacheBust] = useState(() => Date.now());

  const fetchCurrent = async () => {
    try {
      const r = await api.get('/settings');
      const stored = (r.data?.data?.company_logo || '').trim();
      setLogoUrl(stored);
    } catch {
      setLogoUrl('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCurrent(); }, []);

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large (max 2 MB)');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Only PNG, JPG or WEBP files are supported');
      return;
    }
    setBusy(true);
    const tid = toast.loading('Uploading…');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/settings/company-logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(r.data?.data?.url || '');
      setCacheBust(Date.now());
      toast.success('Logo updated', { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed', { id: tid });
    } finally {
      setBusy(false);
      // Reset the input so re-selecting the same filename still triggers
      // onChange.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the company logo from all PDFs?')) return;
    setBusy(true);
    try {
      await api.delete('/settings/company-logo');
      setLogoUrl('');
      toast.success('Logo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  const previewSrc = logoUrl ? `${logoUrl}?v=${cacheBust}` : '';

  return (
    <Card data-testid="settings-company-logo-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-primary" />
          Company Logo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Shown on every in-house PDF — hosting quotes, AMC quotes, and
          payslips. PNG, JPG or WEBP, max 2 MB. Recommended size:
          200×80 px (transparent background looks best).
        </p>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-40 h-20 border rounded-md bg-muted/30 flex items-center justify-center overflow-hidden"
               data-testid="settings-logo-preview">
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : previewSrc ? (
              <img src={previewSrc} alt="Current logo"
                   className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-[10px] text-muted-foreground text-center px-2">
                No logo<br />(text-only header)
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp"
                   className="hidden" onChange={handleFile} data-testid="settings-logo-input" />
            <Button onClick={handlePick} disabled={busy} data-testid="settings-logo-upload">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {logoUrl ? 'Replace logo' : 'Upload logo'}
            </Button>
            {logoUrl && (
              <Button variant="ghost" onClick={handleRemove} disabled={busy}
                      className="text-destructive hover:text-destructive"
                      data-testid="settings-logo-remove">
                <Trash2 className="h-4 w-4 mr-2" />
                Remove logo
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyLogoCard;
